import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import LocaleSwitch from './LocaleSwitch.jsx';
import { upper } from '../i18n/greek.js';
import { formatEventDateShort, formatEventWeekday } from './event-date.js';

const LOGO = '/logos/logo.svg';
const TICKER = 'Book Your Seat';

/**
 * The top of both surfaces: accent rule, logo, date line, headline, ticker.
 *
 * One component because the two had drifted into looking like different
 * products — the microsite cut a border between its header and hero, spaced
 * them further apart, and rendered the headline in flat white where the app
 * gives the second half the brand green.
 *
 * The whole block is a single black field with no internal border, which is
 * what makes the logo, the headline and the ticker read as one masthead rather
 * than three stacked bars.
 *
 * `children` is for what only the web needs — the date/venue detail and the
 * scroll-to-register button. The app puts those elsewhere, so they are passed
 * in rather than flagged off.
 */
export default function EventHero({ event, children }) {
  const { t, i18n } = useTranslation();
  const dateShort = formatEventDateShort(event?.event_date);

  /*
   * The ticker carries the day and the date, so it is built per render rather
   * than being a module constant — both come from the CMS and used to be
   * hardcoded. English in both languages, as it has always been: the strip
   * reads as a brand mark, not as copy.
   *
   * Which is why the place comes from `venue_en` rather than the localised
   * `venue`: «ΠΕΙΡΑΙΩΣ 260» inside an otherwise Latin line reads as a mistake,
   * and the English form also sidesteps toUpperCase, which would keep the tonos
   * that Greek drops when capitalised. Falls back to the localised name if the
   * API is an older build that does not send it.
   */
  const venueLatin = event?.venue_en || event?.venue;
  const stamp = [formatEventWeekday(event?.event_date, 'en'), dateShort].filter(Boolean).join(' ');
  /*
   * One repeating unit, one separator family.
   *
   * A middot joins the phrase to the date, and the same mark — spaced wider —
   * divides one repetition from the next. The pipe this used to carry read as a
   * table column rule in a display face, and pairing it with a middot between
   * repetitions gave the strip two competing separators.
   *
   * Uppercased here, not by the `uppercase` class: that utility is switched off
   * under html[lang='el'] (see tokens.css) because CSS capitalisation keeps the
   * tonos Greek drops, so on the Greek site the class does nothing.
   *
   * Through `upper` rather than toUpperCase, even though the strip is meant to
   * be Latin: when `venue_en` is missing the fallback puts a Greek name in here,
   * and toUpperCase rendered «Πειραιώς 260» as «ΠΕΙΡΑΙΏΣ 260» — the accent
   * Greek drops, on screen. Latin text is unaffected either way.
   */
  const unit = upper(
    [TICKER, stamp, venueLatin].filter(Boolean).join(' · '),
    i18n.resolvedLanguage || i18n.language
  );

  /*
   * An even number of identical items, because the animation translates by
   * exactly -50%: the second half has to land where the first began or the loop
   * visibly jumps.
   */
  const REPEATS = 8;

  return (
    <div className="relative bg-black overflow-hidden safe-top">
      <div className="h-1 w-full bg-brand" />

      {/*
        The logo leads, and is the largest thing here — larger than the headline
        beneath it. It no longer shares the row with the date, which moved into
        the hero where it reads as information rather than as a tag.
      */}
      <div className="site-shell px-5 pt-6 pb-4 flex items-center justify-between gap-4">
        <img src={LOGO} alt="The Monday Festival" className="h-12 sm:h-20 w-auto" />
        <LocaleSwitch />
      </div>

      <div className="site-shell px-5 pt-1 pb-8">
        {/*
          No date here: the ticker below carries the day and the date, and the
          web hero repeats them again in its own detail line. Three times on one
          screen was two too many.
        */}

        {/*
          Capped well below the logo's height so the logo stays the largest
          thing in the masthead. The cap is what does the work: 11vw uncapped
          reached 76px on a laptop and the two-line headline then read as
          bigger than the logo above it, which is the opposite of the intent.
          The lower bound keeps the phone size roughly where it was.
        */}
        <h1 className="display-lg text-white text-[clamp(34px,9vw,46px)] leading-[1.05]">
          {t('home.heroLead')}
          {/*
            `block`, not a <br />: the accent half starts its own line at every
            width, and a hard break would have fought the wrapping instead of
            surviving it on a narrow phone.
          */}
          <span className="text-brand block">{t('home.heroAccent')}</span>
        </h1>

        {/*
          The venue, owned here rather than by each surface. The web used to
          render its own pin in `children`; two copies of one fact is how the
          app and the site drifted apart in the first place.

          Localised — «Πειραιώς 260» in Greek, "Peiraios 260" in English — while
          the ticker above stays Latin in both.
        */}
        {event?.venue && (
          <p className="flex items-center gap-2 mt-4 text-white/70 text-sm font-body">
            <MapPin className="w-4 h-4 text-brand shrink-0" aria-hidden="true" />
            {event.venue}
          </p>
        )}

        {children}
      </div>

      <div className="ticker-wrap bg-brand py-2">
        <div className="ticker-inner">
          {Array.from({ length: REPEATS }, (_, i) => (
            <span
              key={i}
              className="inline-block font-heading font-black uppercase tracking-widest text-black text-sm"
            >
              {unit}
              {/*
                The divider carries the spacing itself — equal margin on both
                sides, and no padding on the item — so the gap is the same
                between two repetitions as it is anywhere else in the run. The
                old markup padded each span *and* ended each repetition with a
                separator, which made the rhythm depend on where a span
                happened to break.
              */}
              <span aria-hidden="true" className="mx-7 text-black/40">
                ·
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
